/**
 * Semantic foreground context for app surfaces.
 *
 * Root cause of navy-on-glass: `body { color: var(--wag-dark) }` (#1E2A44) inherits
 * into portaled dark glass (modals/sheets) and into HomeV2GlassCard. Shared surface
 * primitives must set both background and a default foreground so body copy,
 * labels, and captions inherit readable colors without hardcoding white everywhere.
 *
 * Role tokens (headline / muted / etc.) still come from homeV2Text + *V2Type helpers.
 * This module owns the SURFACE DEFAULT inheritance color only.
 */

import type { CSSProperties } from 'react'
import { homeV2Text } from '@/lib/home/homeV2Theme'
import { colors } from '@/styles/designSystem'

/** Dark glass / modal / sheet / elevated dark card — soft white body. */
export const SURFACE_FG_DARK_GLASS = homeV2Text.body

/** Light elevated card / white modal legacy — navy primary. */
export const SURFACE_FG_LIGHT = colors.textPrimary

/** Muted / helper on dark glass (still readable). */
export const SURFACE_FG_DARK_MUTED = homeV2Text.muted

/** Muted / helper on light surfaces. */
export const SURFACE_FG_LIGHT_MUTED = colors.textSecondary

/** Disabled on dark glass. */
export const SURFACE_FG_DARK_DISABLED = 'rgba(255, 255, 255, 0.42)'

/** Disabled on light surfaces. */
export const SURFACE_FG_LIGHT_DISABLED = 'rgba(28, 28, 30, 0.38)'

/** Status roles (shared across dark glass). */
export const SURFACE_FG_STATUS = {
  destructive: '#FECACA',
  warning: homeV2Text.body,
  success: '#BBF7D0',
} as const

export type SurfaceForegroundKind = 'darkGlass' | 'lightElevated'

export const surfaceForegroundStyle: Record<
  SurfaceForegroundKind,
  CSSProperties
> = {
  darkGlass: { color: SURFACE_FG_DARK_GLASS },
  lightElevated: { color: SURFACE_FG_LIGHT },
}

/** @deprecated Prefer surfaceForegroundStyle.darkGlass — alias for modal shell. */
export const v2ModalShellDefaultTextStyle = surfaceForegroundStyle.darkGlass

/** Data attribute for tests / diagnostics. */
export const SURFACE_FG_ATTR = {
  darkGlass: 'dark-glass',
  lightElevated: 'light-elevated',
} as const

/** Colors that must never appear as inherited/body text on dark glass. */
export const DISALLOWED_DARK_SURFACE_TEXT_TOKENS = [
  'text-gray-900',
  'text-gray-800',
  'text-gray-700',
  'text-gray-600',
  'text-slate-900',
  'text-slate-800',
  'text-neutral-900',
  'text-black',
  'text-[#0',
  'text-[#1E2A44]',
  'text-[#1C1C1E]',
  'color: #1E2A44',
  'color:#1E2A44',
  'color: #1C1C1E',
  'color:#1C1C1E',
] as const
